/**
 * invoices-api.ts — typed feature wrapper + wire→view-model mapping for the
 * Rechnungen-prüfen screen (Story UI-U4; rebuilt from V10-J2).
 *
 * Live wire sources:
 *   - GET /invoices                     → InvoiceListItemDto[] (the table rows)
 *   - GET /invoices/:id/check-detail    → InvoiceCheckDetailDto (the peek facts)
 *   - GET /admin/invoice-check/:id/history → InvoiceHistoryResult (the Verlauf)
 *
 * The mapping lives HERE in the client layer (V10-J AC3 precedent) — components
 * render server verdicts; NO client-side re-categorization (coding-standards #2/#3).
 *
 * [Source: docs/stories/ui-u4-rechnungskontrolle.md AC1/AC4]
 * [Source: apps/web/src/features/invoices/api/invoices-api.ts (V10-J2 predecessor)]
 */

import { apiClient } from '../../../lib/api-client';
import type {
  InvoiceListParams,
  InvoiceHistoryResult,
} from '../../../lib/api-client';
import type { StatusTone } from '../../../components/ui/StatusDot';
import { daysUntil } from '../../../lib/format';
import type {
  DiffCategory,
  InvoiceCheckDetailDto,
  InvoiceCheckStatus,
  InvoiceListItemDto,
  InvoiceType,
} from '@brokly/shared';
import type { InvoiceRow, InvoicePreset } from '../types';

// Re-export for convenience
export type { InvoiceListParams };

// ── German domain labels (design.md: German UI, English identifiers) ──────────

/** Rechnungstyp — full label (title=…) + short cell label (table stays compact). */
export const TYP_LABEL: Record<InvoiceType, string> = {
  recurring: 'Prämienrechnung',
  final_settlement: 'Schlussabrechnung',
};
export const TYP_SHORT: Record<InvoiceType, string> = {
  recurring: 'Prämie',
  final_settlement: 'Schluss',
};

/** K-category short name for the sealed-strip / peek copy. */
export const KATEGORIE_NAME: Record<DiffCategory, string> = {
  K0: 'keine Differenz',
  K1: 'geringfügig',
  K2: 'mittel',
  K3: 'grösser',
};

/** Full-lifecycle German status labels (Dev Notes). "Beanstandet" = our `disputed`. */
export const STATUS_LABEL: Record<InvoiceCheckStatus, string> = {
  received: 'Empfangen',
  matched: 'Zugeordnet',
  checked: 'Geprüft',
  released: 'Freigegeben',
  in_review: 'In Prüfung',
  blocked: 'Blockiert',
  disputed: 'Beanstandet',
  resolved: 'Erledigt',
  paid: 'Bezahlt',
};

/** Status → StatusDot tone (dot + colored text; never a filled pill — design.md §2). */
export const STATUS_TONE: Record<InvoiceCheckStatus, StatusTone> = {
  received: 'neutral',
  matched: 'neutral',
  checked: 'neutral',
  released: 'success',
  in_review: 'warning',
  blocked: 'danger',
  disputed: 'danger',
  resolved: 'success',
  paid: 'success',
};

/** Statuses that mean "needs a human decision" (the Braucht-Entscheid preset). */
const DECIDE_STATUSES: ReadonlySet<InvoiceCheckStatus> = new Set([
  'in_review',
  'blocked',
  'disputed',
]);

/** Statuses that mean "auto-released / done" (the Automatisch-freigegeben preset). */
const AUTO_STATUSES: ReadonlySet<InvoiceCheckStatus> = new Set([
  'released',
  'resolved',
  'paid',
]);

/**
 * Defensive probe for a "Pflichtregel verletzt" flag on the LIST wire — the
 * current InvoiceListItemDto does NOT carry it, so this is structural (unknown +
 * narrow, no `any`): true only when a boolean flag is genuinely present. If the
 * backend wave later adds e.g. `check_pflicht_fail`, it is picked up for free.
 */
export function probePflichtregel(dto: unknown): boolean | undefined {
  if (dto === null || typeof dto !== 'object') return undefined;
  const rec = dto as Record<string, unknown>;
  for (const key of ['check_pflicht_fail', 'pflichtregel', 'mandatory_rule_violated', 'rule_forced']) {
    const v = rec[key];
    if (typeof v === 'boolean') return v;
  }
  return undefined;
}

/** Days since an ISO timestamp (≥0). */
export function wartezeitTage(createdAt: string | null): number {
  const days = daysUntil(createdAt);
  if (days === null) return 0;
  return Math.max(0, -days);
}

/** One wire row → the InvoiceRow view-model (rendering SERVER decisions only). */
export function mapInvoiceToRow(dto: InvoiceListItemDto): InvoiceRow {
  const rawStatus = dto.check_status ?? dto.status;
  const erwartet = dto.expected_premium !== null ? Number(dto.expected_premium) : 0;
  const rechnung =
    dto.invoiced_premium !== null
      ? Number(dto.invoiced_premium)
      : dto.amount_gross !== null
        ? Number(dto.amount_gross)
        : 0;
  return {
    id: dto.id,
    nr: dto.invoice_number ?? dto.id.slice(0, 8).toUpperCase(),
    kunde: dto.kunde_name ?? '-',
    versicherer: dto.carrier_name ?? dto.carrier_uid ?? '-',
    police: dto.policy_number ?? '-',
    policyId: dto.policy_id,
    subjectId: dto.subject_id,
    subjectType: dto.subject_type,
    typ: dto.invoice_type,
    erwartet,
    rechnung,
    differenz: rechnung - erwartet,
    pct: dto.deviation_percent !== null ? Number(dto.deviation_percent) : 0,
    kategorie: dto.check_category,
    pflichtregel: probePflichtregel(dto),
    rawStatus,
    wartezeitTage: wartezeitTage(dto.created_at),
    createdAt: dto.created_at,
  };
}

/** True when the row is currently auto-released / sealed (K0/K1 + terminal status). */
export function isSealed(row: InvoiceRow): boolean {
  return AUTO_STATUSES.has(row.rawStatus);
}

/**
 * Filter presets (client-side, over SERVER-provided fields ONLY — no category
 * re-computation). Braucht Entscheid = decision statuses OR unmatched.
 */
export const INVOICE_PRESETS: InvoicePreset[] = [
  { key: 'alle', label: 'Alle', match: () => true },
  {
    key: 'entscheid',
    label: 'Braucht Entscheid',
    match: (r) => DECIDE_STATUSES.has(r.rawStatus) || r.policyId === null,
  },
  {
    key: 'auto',
    label: 'Automatisch freigegeben',
    match: (r) => AUTO_STATUSES.has(r.rawStatus),
  },
];

// ── CSV export (client-side, filtered rows) ───────────────────────────────────

const CSV_HEADER =
  'Rechnungs-Nr.;Kunde;Versicherer;Police;Typ;Erwartet;Rechnung;Differenz;Differenz %;Kategorie;Wartezeit (Tage);Status';

/** Escape a CSV field for the semicolon dialect (quote when it contains ; " or newline). */
function csvCell(value: string): string {
  if (/[";\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/**
 * Serialize the (already-filtered) rows to a CH-Excel CSV string: semicolon
 * separator, UTF-8 BOM, MACHINE number columns kept as `1234.50` (no de-CH
 * grouping — re-importable). [Source: Dev Notes — Bulk/Exportieren]
 */
export function toCsv(rows: InvoiceRow[]): string {
  const lines = rows.map((r) =>
    [
      r.nr,
      r.kunde,
      r.versicherer,
      r.police,
      TYP_SHORT[r.typ],
      r.erwartet.toFixed(2),
      r.rechnung.toFixed(2),
      (r.differenz >= 0 ? '+' : '') + r.differenz.toFixed(2),
      r.pct.toFixed(1),
      r.kategorie ?? '-',
      String(r.wartezeitTage),
      STATUS_LABEL[r.rawStatus],
    ]
      .map(csvCell)
      .join(';'),
  );
  return `﻿${CSV_HEADER}\n${lines.join('\n')}`;
}

// ── Fetch wrappers ────────────────────────────────────────────────────────────

/** Fetch + map the Rechnungen rows (GET /invoices). */
export async function fetchInvoiceRows(params: InvoiceListParams = {}): Promise<InvoiceRow[]> {
  const response = await apiClient.invoices.list(params);
  return response.items.map(mapInvoiceToRow);
}

/** Fetch the peek facts (GET /invoices/:id/check-detail). */
export function fetchCheckDetail(id: string): Promise<InvoiceCheckDetailDto> {
  return apiClient.invoices.checkDetail(id);
}

/**
 * Fetch the Verlauf (GET /admin/invoice-check/:id/history). 403/404 (or any
 * read failure) resolve to `null` so the section HIDES gracefully — the timeline
 * is a non-critical, permission-gated surface (defensive rule AC 5), never an
 * error state that breaks the peek.
 */
export async function fetchHistory(id: string): Promise<InvoiceHistoryResult | null> {
  try {
    return await apiClient.adminInvoiceCheck.history(id);
  } catch {
    return null;
  }
}
